import React, { FunctionComponent, SyntheticEvent, useEffect, useState } from 'react'
import { useTranslation } from '../../utils/convergence'
import { usePersonalization } from '../../utils/personalization'
import { PERSONALIZATION_POST_CAPTURE_TIMEOUT, PostCheckoutFlow, CheckoutStatus, AssetId } from '../../model'
import { useTracking } from './tracking'
import { useCheckoutToken, useLazyGetStatusQuery, useCheckoutStatus, useMerchantDisplayNameOrName, useConsumerSelector, ConsumerProfile } from '../../state'

import styles from './styles.module.css'
import { isOrderStatusError, isOrderStatusResponse, OrderErrorType, OrderStatus } from '@afterpaytouch/order-status-api'
import { getPostCheckoutLayout } from './utils'
import { useGetLocaleWithFallback, useSignalCheckoutCompletion } from '../../utils/post-checkout'
import { TitleWithBody } from '../Asset'

interface Props {
  onTimerChange?: (timeout: number | null) => void
  onStateChange?: (shouldShowLoader: boolean) => void
}

export const PostCheckout: FunctionComponent<Props> = ({ onTimerChange = () => {}, onStateChange = () => {} }) => {
  const { t } = useTranslation()
  const { trackAssetImpression, trackAssetClick, trackBackToStore, trackMerchantCaptureResult } = useTracking()
  const token = useCheckoutToken()
  const { profile } = useConsumerSelector()
  const { postCheckout } = usePersonalization()
  const [getOrderStatus, { data, isUninitialized }] = useLazyGetStatusQuery()
  const signalCheckoutCompletion = useSignalCheckoutCompletion()
  const merchantDisplayName = useMerchantDisplayNameOrName()

  const checkoutStatus = useCheckoutStatus()
  const isClosed = checkoutStatus === CheckoutStatus.Closed

  const { enabled, asset, onAssetImpression, onAssetNegativeActionClick, onAssetPositiveActionClick, flow, showComponentState, stepState } = postCheckout
  const { id } = asset
  const isPreCapture = flow === PostCheckoutFlow.PreCapture
  const isPostCapture = flow === PostCheckoutFlow.PostCapture
  const { setStep } = stepState
  const { showComponent, setShowComponent } = showComponentState
  const [trackedImpression, setTrackedImpression] = useState(false)
  const [actioned, setActioned] = useState(false)
  const [clickedBackToStore, setClickedBackToStore] = useState(false)
  const [showProgressBar, setShowProgressBar] = useState(isPreCapture)
  const [selected, setSelected] = useState('')
  const [consumerProfile, setConsumerProfile] = useState<ConsumerProfile>()
  const givenNames = consumerProfile?.givenNames
  const localeWithFallback = useGetLocaleWithFallback()
  const shouldDisabledCTAs = isClosed || clickedBackToStore

  const handlePrimaryAction = (e: SyntheticEvent<HTMLInputElement>): void => {
    if (!clickedBackToStore) {
      setActioned(true)
      setShowProgressBar(false)
      setSelected(e.currentTarget.dataset.label ?? '')
      trackAssetClick(e.currentTarget.value)
      onTimerChange(onAssetPositiveActionClick(e.currentTarget.value))
    }
  }

  const handleNegativeAction = (e: SyntheticEvent<HTMLInputElement>): void => {
    if (!clickedBackToStore) {
      setActioned(true)
      setShowProgressBar(false)
      trackAssetClick(e.currentTarget.value)
      onTimerChange(onAssetNegativeActionClick(e.currentTarget.value))
    }
  }

  const handleSkipAction = (): void => {
    if (!clickedBackToStore) {
      setClickedBackToStore(true)
      setShowProgressBar(false)
      trackBackToStore()
      onTimerChange(0)
    }
  }

  useEffect(() => {
    let postCaptureTimeout

    const handlePostCapture = (status: 'show' | 'exit'): void => {
      clearTimeout(postCaptureTimeout)
      switch (status) {
        case 'show':
          setStep(2)
          setShowComponent(true)
          onStateChange(false)
          break
        case 'exit':
          onTimerChange(0)
          break
      }
    }

    if (enabled && isPostCapture) {
      const isOrderCaptureStatusAvailable = isOrderStatusResponse(data)
      const isOrderCaptureStatusError = isOrderStatusError(data)

      if (isUninitialized) {
        signalCheckoutCompletion()
        getOrderStatus({ orderToken: token })
      }

      if (!isOrderCaptureStatusAvailable && !isOrderCaptureStatusError) {
        postCaptureTimeout = setTimeout(() => {
          handlePostCapture('exit')
        }, PERSONALIZATION_POST_CAPTURE_TIMEOUT)
      }

      if (isOrderCaptureStatusAvailable) {
        trackMerchantCaptureResult(data?.status)
        if (data?.status === OrderStatus.Approved) {
          handlePostCapture('show')
        } else {
          handlePostCapture('exit')
        }
      }

      if (isOrderCaptureStatusError) {
        trackMerchantCaptureResult(OrderErrorType[data?.errorType] ?? 'unknown')
        handlePostCapture('exit')
      }
    }

    return () => {
      clearTimeout(postCaptureTimeout)
    }
  }, [enabled, isPostCapture, data, isUninitialized])

  useEffect(() => {
    if (showComponent && !trackedImpression) {
      setTrackedImpression(true)
      trackAssetImpression()
      onAssetImpression()
    }
  }, [showComponent, trackedImpression])

  useEffect(() => {
    if (profile !== undefined && consumerProfile === undefined) {
      setConsumerProfile(profile)
    }
  }, [profile, consumerProfile])

  if (!showComponent) {
    return null
  }

  const Asset = getPostCheckoutLayout(id)
  const isCashAppMessageAsset = id === AssetId.CashAppOrderServicingMessage202503
  const componentClassname = !isCashAppMessageAsset ? styles.scrollableSection : undefined

  return (
    <div className={componentClassname}>
      <div className={styles.component}>
        <Asset
          flow={flow}
          primaryTitle={t(localeWithFallback('titlePrimary'), '')}
          secondaryTitle={t(localeWithFallback('titleSecondary'), '')}
          heroTitle={t(localeWithFallback('heroTitle'), '', { givenNames })}
          thankyouTitle={t<'titleSuccess', { returnObjects: true }, TitleWithBody[]>(localeWithFallback('titleSuccess'), '', {
            returnObjects: true,
          })}
          finePrint={t(localeWithFallback('finePrint'), '')}
          privacyPolicyUrl={t('common:privacyPolicyUrl')}
          footerLinkTitle={t(localeWithFallback('link'), '', {
            ...(actioned && { context: 'actioned' }),
            merchantDisplayName,
          })}
          postCaptureConfirmTitle={t('postcheckout:postCaptureConfirm.title', '')}
          postCaptureConfirmDescription='postcheckout:postCaptureConfirm:description'
          onPrimaryActionClick={handlePrimaryAction}
          onNegativeActionClick={handleNegativeAction}
          onSkipActionClick={handleSkipAction}
          showProgressBar={showProgressBar}
          actioned={actioned}
          disabled={shouldDisabledCTAs}
          selectedOption={selected}
          assetId={id}
          loaderTitle={t(localeWithFallback('loaderTitle'), '', { merchantDisplayName })}
        />
      </div>
    </div>
  )
}
